import React, { useState, useEffect } from "react";
import {
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Box,
  Button,
  Grid,
  TextField,
  Typography,
  CircularProgress,
  InputAdornment,
  Backdrop
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import NavigateNextRoundedIcon from "@mui/icons-material/NavigateNextRounded";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { TimePicker } from "@mui/x-date-pickers/TimePicker";
import { Formik, Form } from "formik";
import * as Yup from "yup";
import dayjs from "dayjs";
import { toast } from "react-toastify";

// API
import { eopdAPI } from "../../../../../services/e-opd.api";

// Styles
import "../OpdStepper.css";
import { calculateAge, formatDateAge } from "../../../../../helpers/common-helper";

/** Khaali read-only value ki jagah muted dash — blank row toota hua lagta hai. */
const show = (v) => (v === null || v === undefined || String(v).trim() === "" ? "—" : v);

export default function Step1VisitDemographics({ nextStep, isFirstTimeAbha = true, onStep1Submitted }) {
  const [loading, setLoading] = useState(false);
  const [recordNo, setRecordNo] = useState("");
  const [loadingDisplayId, setLoadingDisplayId] = useState(false);
  const [formValues, setFormValues] = useState({
    pmjayId: "",
    ashaName: "",
    ashaPhone: "",
    date: dayjs().format("YYYY-MM-DD"),
    time: dayjs().format("HH:mm"),
  });

  // Accordion open/close states
  const [expanded, setExpanded] = useState({
    facility: true,
    demographics: true,
    asha: true,
    timing: true,
  });

  // State to hold facility details fetched from localStorage of "e-OPD_loggedinUserdetails"
  const [facilityDetails, setFacilityDetails] = useState({
    facilityName: "",
    hfr_id: "",
    name: "",
    hpr_id: "",
    nin_id: ""
  });

  // State to hold demographics details fetched from localStorage or GET step 1 API
  const [demographics, setDemographics] = useState({
    name: "",
    dob: "",
    gender: "",
    address: "",
    phoneNo: "",
    abhaNo: "",
    abha_address: "",
  });

  const handleAccordionChange = (panel) => (event, isExpanded) => {
    setExpanded((prev) => ({ ...prev, [panel]: isExpanded }));
  };

  // Fetch record no (display ID) on mount for first-time ABHA, or load step 1 data if returning
  useEffect(() => {
    let active = true;

    if (isFirstTimeAbha) {
      // 4.1.b: When user comes to step 1 on first time by ABHA verification, call getDisplayId
      const fetchRecordId = async () => {
        setLoadingDisplayId(true);
        try {
          const res = await eopdAPI.getDisplayId();
          if (!active) return;
          if (res && res?.success && res?.display_id) {
            setRecordNo(res?.display_id);
            localStorage.setItem("currentOpdDisplayId", res?.display_id);
          } else {
            toast.error("Failed to fetch Record No.");
            setRecordNo("");
          }
        } catch (err) {
          console.error("Error fetching display ID:", err);
          if (active) toast.error("Failed to fetch Record No.");
          setRecordNo("");
        } finally {
          if (active) setLoadingDisplayId(false);
        }
      };
      fetchRecordId();
    } else {
      // 4.1.a & 4.2: Returning to Step 1 or from Visit History: do NOT call getDisplayId.
      const savedDisplayId = localStorage.getItem("currentOpdDisplayId") || "";
      setRecordNo(savedDisplayId);

      if (savedDisplayId) {
        const fetchStep1Data = async () => {
          setLoadingDisplayId(true);
          try {
            const res = await eopdAPI.getOpdStepData(savedDisplayId, 1);
            if (!active) return;
            if (res && res.success && res.data) {
              const d = res.data;
              setDemographics({
                name: d.full_name || "",
                dob: d.dob || "",
                gender: d.gender_name || (d.gender === 1 ? "Male" : d.gender === 2 ? "Female" : ""),
                address: d.address || "",
                phoneNo: d.mobile_no || "",
                abhaNo: d.abha_number || "",
                abha_address: d.abha_address || "",
              });
              setFormValues({
                pmjayId: d.pmjay_id || "",
                ashaName: d.asha_name || "",
                ashaPhone: d.asha_mobile || "",
                date: d.visit_dt ? dayjs(d.visit_dt).format("YYYY-MM-DD") : dayjs().format("YYYY-MM-DD"),
                time: d.visit_time ? dayjs(d.visit_time, ["HH:mm:ss", "HH:mm"]).format("HH:mm") : dayjs().format("HH:mm"),
              });
            }
          } catch (err) {
            console.error("Error fetching step 1 data:", err);
          } finally {
            if (active) setLoadingDisplayId(false);
          }
        };
        fetchStep1Data();
      }
    }

    return () => {
      active = false;
    };
  }, [isFirstTimeAbha]);

  // Load facility and demographic details from localStorage
  useEffect(() => {
    // 1. Fetch Facility Details from "e-OPD_loggedinUserdetails"
    const loggedInUserRaw = localStorage.getItem("e-OPD_loggedinUserdetails");
    if (loggedInUserRaw) {
      try {
        const user = JSON.parse(loggedInUserRaw);
        setFacilityDetails({
          facilityName: user?.facilityName || "",
          hfr_id: user?.hfr_id || "",
          name: user?.firstname || "",
          hpr_id: user?.hpr_id || "",
          nin_id: user?.nin_id || ""
        });
      } catch (e) {
        console.error("Error parsing loggedInUserdetails:", e);
      }
    }

    // 2. Fetch Demographic Details from "orignalBeneficiaryData" only if first-time ABHA
    if (isFirstTimeAbha) {
      const beneficiaryDataRaw = localStorage.getItem("orignalBeneficiaryData");
      if (beneficiaryDataRaw) {
        try {
          const data = JSON.parse(beneficiaryDataRaw);
          setDemographics({
            name: data?.name || "",
            dob: data?.dob || "",
            gender: data?.fullgendername || "",
            address: data?.address || "",
            phoneNo: data?.mobile || "",
            abhaNo: data?.abhaNumber || "",
            abha_address: data?.preferredAbhaAddress || ""
          });
        } catch (err) {
          console.error("Error parsing original beneficiary details:", err);
        }
      }
    }
  }, [isFirstTimeAbha]);
  const displayAge = demographics.dob ? calculateAge(formatDateAge(demographics.dob)) : "";

  const validationSchema = Yup.object().shape({
    pmjayId: Yup.string()
      .nullable()
      .matches(/^[A-Z0-9]*$/, {
        message: "Only capital letters and numbers are allowed",
        excludeEmptyString: true,
      }),
    ashaName: Yup.string()
      .nullable()
      .matches(/^[a-zA-Z\s]*$/, {
        message: "Only alphabets are allowed",
        excludeEmptyString: true,
      }),
    ashaPhone: Yup.string()
      .nullable()
      .matches(/^[6-9]\d{9}$/, {
        message: "Must be a 10-digit number starting with 6-9",
        excludeEmptyString: true,
      }),
    date: Yup.date()
      .nullable()
      .max(new Date(), "Date cannot be in the future"),
    time: Yup.string().nullable(),
  });

  const handleSubmit = async (values, { setSubmitting }) => {
    setLoading(true);
    try {
      // Map gender: Male/M/1 -> 1, Female/F/2 -> 2
      const genderStr = String(demographics.gender || "").trim().toLowerCase();
      const genderVal = (genderStr === "male" || genderStr === "m" || genderStr === "1") ? 1 :
        (genderStr === "female" || genderStr === "f" || genderStr === "2") ? 2 : 1;

      // Build payload containing the exact keys and formatting from the API specification
      const payload = {
        display_id: recordNo,
        full_name: demographics.name,
        dob: demographics.dob,
        gender: genderVal,
        mobile_no: demographics.phoneNo,
        abha_number: demographics.abhaNo,
        abha_address: demographics.abha_address,
        address: demographics.address,
        visit_dt: values.date ? dayjs(values.date).format("DD-MM-YYYY") : dayjs().format("DD-MM-YYYY"),
        visit_time: values.time ? dayjs(values.time, ["HH:mm", "hh:mm A"]).format("HH:mm") : dayjs().format("HH:mm"),
        asha_name: values.ashaName || "",
        asha_mobile: values.ashaPhone || "",
        pmjay_id: values.pmjayId || "",
      };

      const response = await eopdAPI.saveOpdStep(1, payload);

      if (response && response.success) {
        toast.success(response.message || "Visit details submitted successfully!");
        onStep1Submitted?.();
        nextStep();
      } else {
        toast.error(response.message || "Failed to submit visit details.");
      }
    } catch (error) {
      console.error("Error submitting visit details:", error);
      const errMsg = error.response?.data?.message || error.message || "Failed to submit visit details.";
      toast.error(errMsg);
    } finally {
      setLoading(false);
      setSubmitting(false);
    }
  };

  return (
    <Formik
      initialValues={formValues}
      validationSchema={validationSchema}
      onSubmit={handleSubmit}
      enableReinitialize
    >
      {({ values, errors, touched, handleChange, handleBlur, setFieldValue }) => (
        <Form>
          <Backdrop
            sx={{ color: "#fff", zIndex: (theme) => theme.zIndex.drawer + 1 }}
            open={loading}
          >
            <CircularProgress color="inherit" />
          </Backdrop>
          <Typography className="opd-step-title">
            Visit, Facility & Demographic Details
          </Typography>

          {/* 1. Facility & Visit Details Accordion */}
          <Accordion
            expanded={expanded.facility}
            onChange={handleAccordionChange("facility")}
            className={`opd-accordion ${expanded.facility ? "opd-accordion-expanded" : ""}`}
          >
            <AccordionSummary expandIcon={<ExpandMoreIcon />} className="opd-accordion-summary">
              <Box>
                <Typography className="opd-accordion-title">Facility & Visit Details</Typography>
                {/* <Typography className="opd-accordion-subtitle">
                  Identifies the Health & Wellness Centre and the care team for this visit.
                </Typography> */}
              </Box>
            </AccordionSummary>
            <AccordionDetails className="opd-accordion-details">
              <Grid container spacing={3}>
                <Grid size={{ xs: 6, md: 8 }}>
                  <Box className="opd-value-group">
                    <Typography className="opd-value-label">AAM SHC Name</Typography>
                    <Typography className="opd-value-content">{show(facilityDetails.facilityName)}</Typography>
                  </Box>
                </Grid>
                <Grid size={{ xs: 6, md: 4 }}>
                  <Box className="opd-value-group">
                    <Typography className="opd-value-label">HFR Facility ID</Typography>
                    <Typography className="opd-value-content">{show(facilityDetails.hfr_id ? facilityDetails.hfr_id : "NA")}</Typography>
                  </Box>
                </Grid>
                <Grid size={{ xs: 6, md: 4 }}>
                  <Box className="opd-value-group">
                    <Typography className="opd-value-label">CHO Name</Typography>
                    <Typography className="opd-value-content">{show(facilityDetails.name)}</Typography>
                  </Box>
                </Grid>
                <Grid size={{ xs: 6, md: 4 }}>
                  <Box className="opd-value-group">
                    <Typography className="opd-value-label">HPR Provider ID</Typography>
                    <Typography className="opd-value-content">{show(facilityDetails.hpr_id ? facilityDetails.hpr_id : "NA")}</Typography>
                  </Box>
                </Grid>
                <Grid size={{ xs: 6, md: 4 }}>
                  <Box className="opd-value-group">
                    <Typography className="opd-value-label">NIN ID</Typography>
                    <Typography className="opd-value-content">{show(facilityDetails.nin_id ? facilityDetails.nin_id : "NA")}</Typography>
                  </Box>
                </Grid>
              </Grid>
            </AccordionDetails>
          </Accordion>

          {/* 2. Demographic Details Accordion */}
          <Accordion
            expanded={expanded.demographics}
            onChange={handleAccordionChange("demographics")}
            className={`opd-accordion ${expanded.demographics ? "opd-accordion-expanded" : ""}`}
          >
            <AccordionSummary expandIcon={<ExpandMoreIcon />} className="opd-accordion-summary">
              <Box>
                <Typography className="opd-accordion-title">Demographic Details</Typography>
                {/* <Typography className="opd-accordion-subtitle">
                  Core patient identity. Name, age and gender are required to proceed.
                </Typography> */}
              </Box>
            </AccordionSummary>
            <AccordionDetails className="opd-accordion-details">
              <Grid container spacing={3}>
                <Grid size={{ xs: 12, md: 6 }}>
                  <Box className="opd-value-group">
                    <Typography className="opd-value-label">Name</Typography>
                    <Typography className="opd-value-content">{show(demographics.name)}</Typography>
                  </Box>
                </Grid>
                <Grid size={{ xs: 6, md: 3 }}>
                  <Box className="opd-value-group">
                    <Typography className="opd-value-label">Age</Typography>
                    <Typography className="opd-value-content">{show(displayAge)}</Typography>
                  </Box>
                </Grid>
                <Grid size={{ xs: 6, md: 3 }}>
                  <Box className="opd-value-group">
                    <Typography className="opd-value-label">Sex</Typography>
                    <Typography className="opd-value-content">{show(demographics.gender)}</Typography>
                  </Box>
                </Grid>
                <Grid size={{ xs: 12 }}>
                  <Box className="opd-value-group">
                    <Typography className="opd-value-label">Address</Typography>
                    <Typography className="opd-value-content" sx={{ textTransform: "capitalize" }}>{show(demographics.address)}</Typography>
                  </Box>
                </Grid>
                <Grid size={{ xs: 12, sm: 4 }}>
                  <Box className="opd-value-group">
                    <Typography className="opd-value-label">Phone No.</Typography>
                    <Typography className="opd-value-content">{show(demographics.phoneNo)}</Typography>
                  </Box>
                </Grid>
                <Grid size={{ xs: 12, sm: 4 }}>
                  <Box className="opd-value-group">
                    <Typography className="opd-value-label">ABHA No.</Typography>
                    <Typography className="opd-value-content">{show(demographics.abhaNo)}</Typography>
                  </Box>
                </Grid>
                <Grid size={{ xs: 12, sm: 4 }}>
                  <Box className="opd-input-group">
                    <Typography className="opd-field-label">PMJAY ID (optional)</Typography>
                    <TextField
                      fullWidth
                      size="small"
                      placeholder="Enter PMJAY ID"
                      name="pmjayId"
                      value={values.pmjayId}
                      onChange={(e) => setFieldValue("pmjayId", e.target.value.toUpperCase())}
                      onBlur={handleBlur}
                      error={Boolean(errors.pmjayId)}
                      helperText={errors.pmjayId}
                      className="opd-input-field"
                    />
                  </Box>
                </Grid>
              </Grid>
            </AccordionDetails>
          </Accordion>

          {/* 3. ASHA Details Accordion */}
          <Accordion
            expanded={expanded.asha}
            onChange={handleAccordionChange("asha")}
            className={`opd-accordion ${expanded.asha ? "opd-accordion-expanded" : ""}`}
          >
            <AccordionSummary expandIcon={<ExpandMoreIcon />} className="opd-accordion-summary">
              <Box>
                <Typography className="opd-accordion-title">ASHA Details</Typography>
              </Box>
            </AccordionSummary>
            <AccordionDetails className="opd-accordion-details">
              <Grid container spacing={3}>
                <Grid size={{ xs: 12, sm: 6 }}>
                  <Box className="opd-input-group">
                    <Typography className="opd-field-label">ASHA Name (optional)</Typography>
                    <TextField
                      fullWidth
                      size="small"
                      placeholder="Enter ASHA name"
                      name="ashaName"
                      value={values.ashaName}
                      onChange={(e) => setFieldValue("ashaName", e.target.value.replace(/[^a-zA-Z\s]/g, ""))}
                      onBlur={handleBlur}
                      error={Boolean(errors.ashaName)}
                      helperText={errors.ashaName}
                      className="opd-input-field"
                    />
                  </Box>
                </Grid>
                <Grid size={{ xs: 12, sm: 6 }}>
                  <Box className="opd-input-group">
                    <Typography className="opd-field-label">ASHA Phone No (optional)</Typography>
                    <TextField
                      fullWidth
                      size="small"
                      placeholder="Enter phone number"
                      name="ashaPhone"
                      value={values.ashaPhone}
                      onChange={(e) => {
                        const val = e.target.value.replace(/\D/g, "").substring(0, 10);
                        setFieldValue("ashaPhone", val);
                      }}
                      onBlur={handleBlur}
                      error={Boolean(errors.ashaPhone)}
                      helperText={errors.ashaPhone}
                      className="opd-input-field"
                    />
                  </Box>
                </Grid>
              </Grid>
            </AccordionDetails>
          </Accordion>

          {/* 4. Visit Timing Accordion */}
          <Accordion
            expanded={expanded.timing}
            onChange={handleAccordionChange("timing")}
            className={`opd-accordion ${expanded.timing ? "opd-accordion-expanded" : ""}`}
          >
            <AccordionSummary expandIcon={<ExpandMoreIcon />} className="opd-accordion-summary">
              <Box>
                <Typography className="opd-accordion-title">Visit Timing</Typography>
              </Box>
            </AccordionSummary>
            <AccordionDetails className="opd-accordion-details">
              <Grid container spacing={3}>
                <Grid size={{ xs: 12, sm: 4 }}>
                  <Box className="opd-input-group">
                    <Typography className="opd-field-label">Date</Typography>
                    <LocalizationProvider dateAdapter={AdapterDayjs}>
                      <DatePicker
                        value={values.date ? dayjs(values.date) : null}
                        maxDate={dayjs()}
                        format="DD-MM-YYYY"
                        onChange={(val) => {
                          setFieldValue("date", val ? val.format("YYYY-MM-DD") : null);
                        }}
                        slotProps={{
                          textField: {
                            fullWidth: true,
                            size: "small",
                            name: "date",
                            onBlur: handleBlur,
                            error: touched.date && Boolean(errors.date),
                            helperText: touched.date && errors.date,
                            className: "opd-input-field",
                          },
                        }}
                      />
                    </LocalizationProvider>
                  </Box>
                </Grid>
                <Grid size={{ xs: 12, sm: 4 }}>
                  <Box className="opd-input-group">
                    <Typography className="opd-field-label">Time</Typography>
                    <LocalizationProvider dateAdapter={AdapterDayjs}>
                      <TimePicker
                        ampm={false}
                        value={values.time ? dayjs(values.time, "HH:mm") : null}
                        onChange={(val) => {
                          setFieldValue("time", val ? val.format("HH:mm") : null);
                        }}
                        slotProps={{
                          textField: {
                            fullWidth: true,
                            size: "small",
                            name: "time",
                            onBlur: handleBlur,
                            error: touched.time && Boolean(errors.time),
                            helperText: touched.time && errors.time,
                            className: "opd-input-field",
                          },
                        }}
                      />
                    </LocalizationProvider>
                  </Box>
                </Grid>
                <Grid size={{ xs: 12, sm: 4 }}>
                  <Box className="opd-input-group">
                    <Typography className="opd-field-label">Record No (auto)</Typography>
                    <TextField
                      disabled
                      fullWidth
                      size="small"
                      value={loadingDisplayId ? "Fetching..." : recordNo}
                      className="opd-input-field"
                      InputProps={{
                        endAdornment: loadingDisplayId ? (
                          <InputAdornment position="end">
                            <CircularProgress size={18} />
                          </InputAdornment>
                        ) : null,
                      }}
                    />
                  </Box>
                </Grid>
              </Grid>
            </AccordionDetails>
          </Accordion>

          {/* Action Footer */}
          <Box sx={{ display: "flex", justifyContent: "flex-end", mt: 4 }}>
            <Button
              type="submit"
              variant="contained"
              className="opd-next-btn"
              disabled={loading}
              endIcon={loading ? <CircularProgress size={20} color="inherit" /> : <NavigateNextRoundedIcon />}
            >
              {loading ? "Saving..." : "Next"}
            </Button>
          </Box>
        </Form>
      )}
    </Formik>
  );
}
